import { logger } from '../utils/logger.js';

const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

let isInitialized = false;
let hasConsent = false;

/**
 * Check if user has given cookie consent
 * @returns {boolean}
 */
function checkConsent() {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('cookie-consent') === 'accepted';
}

/**
 * Load Google Analytics script
 */
function loadGoogleAnalytics() {
  if (!GA_MEASUREMENT_ID) {
    logger.warn('Google Analytics Measurement ID not configured');
    return;
  }

  // Create script element
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure'
  });

  isInitialized = true;
  logger.info('Google Analytics initialized');
}

/**
 * Initialize analytics service
 */
export function initializeAnalytics() {
  hasConsent = checkConsent();
  
  if (hasConsent && !isInitialized) {
    loadGoogleAnalytics();
  }

  // Listen for consent changes
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (e.key === 'cookie-consent') {
        hasConsent = e.newValue === 'accepted';
        if (hasConsent && !isInitialized) {
          loadGoogleAnalytics();
        }
      }
    });
  }
}

/**
 * Track page view
 * @param {string} pagePath - Page path
 * @param {string} [pageTitle] - Page title
 */
export function trackPageView(pagePath, pageTitle) {
  if (!hasConsent || !isInitialized || typeof window === 'undefined') return;

  window.gtag?.('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title
  });

  logger.debug('Page view tracked:', pagePath);
}

/**
 * Track custom event
 * @param {string} eventName - Event name
 * @param {Record<string, any>} [params] - Event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!hasConsent || !isInitialized || typeof window === 'undefined') return;

  window.gtag?.('event', eventName, params);

  logger.debug('Event tracked:', eventName, params);
}

/**
 * Track item view
 * @param {object} item - Item data
 */
export function trackItemView(item) {
  trackEvent('view_item', {
    item_id: item.id,
    item_name: item.name,
    price: item.price,
    item_category: item.category
  });
}

/**
 * Track WhatsApp click
 * @param {object} item - Item data
 */
export function trackWhatsAppClick(item) {
  trackEvent('contact', {
    method: 'whatsapp',
    item_id: item.id,
    item_name: item.name,
    value: item.price
  });
}

/**
 * Track search
 * @param {string} searchTerm - Search term
 */
export function trackSearch(searchTerm) {
  trackEvent('search', {
    search_term: searchTerm
  });
}

/**
 * Track filter usage
 * @param {string} filterType - Filter type
 * @param {string} filterValue - Filter value
 */
export function trackFilter(filterType, filterValue) {
  trackEvent('filter', {
    filter_type: filterType,
    filter_value: filterValue
  });
}
